'use client'

import { useAuth, useClerk } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'
import { type MouseEvent, useCallback } from 'react'
import { useAppLocale } from '@/components/i18n/locale-provider'
import { postAuthLandingPath } from '@/lib/auth/post-auth-landing'
import { isPlainClick } from '@/lib/ui/plain-click'

/** Where a signed-in visitor's "Start free" goes instead of the modal. */
export const SIGNED_IN_HOME_PATH = '/home'
/**
 * The public site's one way in: Clerk's sign-in modal, in place, in its
 * sign-in-or-up flow (`withSignUp`), so a returning member signs in and a
 * new visitor creates an account from the same modal, with no trip to the
 * `/sign-up` page. Both land on the post-auth route, which sends a new
 * account to onboarding and a returning member home before anything paints
 * (Clerk's combined flow does not reliably pick the sign-up redirect), with
 * the page's language so a new account keeps it; a visitor already signed
 * in goes to the app instead.
 */
export function useOpenAuthModal(): () => void {
  const { isSignedIn } = useAuth()
  const { openSignIn } = useClerk()
  const router = useRouter()
  const landing = postAuthLandingPath(useAppLocale())
  return useCallback(() => {
    if (isSignedIn) {
      router.push(SIGNED_IN_HOME_PATH)
      return
    }
    openSignIn({
      withSignUp: true,
      fallbackRedirectUrl: landing,
      signUpFallbackRedirectUrl: landing,
    })
  }, [isSignedIn, landing, openSignIn, router])
}

/**
 * For a real link to `/sign-up` (it works before hydration and without
 * JavaScript): a plain click opens the auth modal in place, while a
 * Ctrl/Cmd/middle click keeps the browser's new tab.
 */
export function useAuthModalLinkClick(): (
  event: MouseEvent<HTMLAnchorElement>
) => void {
  const open = useOpenAuthModal()
  return useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!isPlainClick(event)) {
        return
      }
      event.preventDefault()
      open()
    },
    [open]
  )
}
